import { useMutation } from "react-query";

import { t } from "@/i18n-js/instance";
import type { ImagePostApiResponse } from "@/react/api/imagePostApi";
import { createImagePost, updateImagePost } from "@/react/api/imagePostApi";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import type { ApiError } from "@/react/config/CustomErrors";
import type { ImagePost, Space } from "@/react/types";

import { useImagePostModalStore } from "../../store";
import { formPayload } from "../helpers";
import type { ImagePostFormValues } from "../types";

interface UseImagePostMutationParams {
  onSuccess: (response: ImagePostApiResponse) => void | Promise<void>;
  post: ImagePost;
  space?: Space;
}

export const useImagePostMutation = ({
  space,
  post,
  onSuccess,
}: UseImagePostMutationParams) => {
  const toast = useToast();
  const { setIsSubmitting, uploadedImages } = useImagePostModalStore();
  const isEdit = !!post.slug;

  const options = {
    onSuccess: (response: ImagePostApiResponse) => {
      setIsSubmitting(false);
      void onSuccess(response);
      toast.success(
        t(
          isEdit
            ? "quick_post.toasts.changes_saved"
            : `quick_post.toasts.${response.status}_saved`,
        ),
      );
    },
    onError: (response: ApiError) => {
      setIsSubmitting(false);
      const errorMessage =
        typeof response.errorDetails === "string"
          ? response.errorDetails
          : response.message;
      toast.error(errorMessage, {
        shouldShowCloseButton: true,
        shouldUseProgress: false,
      });
    },
  };
  const { mutate } = useMutation<
    ImagePostApiResponse,
    ApiError,
    ReturnType<typeof formPayload>
  >({
    mutationFn: payload => {
      if (!space) {
        throw new Error("Space not found");
      }

      if (post.slug)
        return updateImagePost({
          spaceId: space.id,
          postSlug: post.slug,
          payload,
        });

      return createImagePost({ spaceId: space.id, payload });
    },
    ...options,
  });

  const onSubmit = (payload: ImagePostFormValues) => {
    if (!space) {
      toast.error(t("something_went_wrong"), {
        shouldShowCloseButton: true,
        shouldUseProgress: false,
      });
      return;
    }

    setIsSubmitting(true);
    mutate(formPayload({ payload, space, uploadedImages }));
  };

  return { onSubmit };
};
